import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { InitialDataResolver } from 'app/app.resolvers';
import {RolesAccessGuard} from "./core/guards/roles.access.guard";

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    //######################################################################################################################################
    //  BLANK AND AUTHENTICATION ROUTES ####################################################################################################
    //######################################################################################################################################

    // Redirect empty path to '/dashboard'
    {
        path: '',
        pathMatch : 'full',
        redirectTo: '/home'
    },

    // Redirect signed-in user to the '/dashboard'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {path: 'signed-in-redirect', pathMatch : 'full', redirectTo: 'home'},

    //######################################################################################################################################
    //  MEMBER PORTAL — PUBLIC REPORTS #####################################################################################################
    //######################################################################################################################################

    //  Página pública do Portal do Membro (acessível SEM login — destina-se a
    //  ser escaneada por QR no celular). Sem canMatch de auth e sem shell/menu
    //  (layout empty, mesmo padrão do login). DEVE vir ANTES de TODOS os
    //  blocos `path: ''` com canMatch de auth: um guard canMatch que retorna
    //  UrlTree (AuthGuard → /login para guests) redireciona imediatamente e
    //  cancela a avaliação das rotas seguintes — se este bloco viesse depois,
    //  um guest seria mandado ao login antes de alcançá-lo. Aqui é o primeiro
    //  candidato concreto para a URL, funcionando autenticado ou não.
    //  URL canônica: /membership/portal?l={linkToken} (contexto membership).
    {
        path: 'membership/portal',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: '', loadChildren: () => import('app/modules/admin/financial/reports/public/public-reports.module').then(m => m.PublicReportsModule)},
        ]
    },

    //  Redirect de QRs/link impressos com a URL ANTIGA (/financial/reports/public)
    //  → nova URL /membership/portal. O Angular PRESERVA queryParams no
    //  redirectTo, então ?l={linkToken} chega íntegro à rota nova. Sem canMatch
    //  de auth (mesma regra do bloco público) e posicionado ANTES dos blocos
    //  `path: ''` com canMatch [AuthGuard] para guests não serem redirecionados
    //  ao login antes de alcançá-lo.
    {
        path: 'financial/reports/public',
        redirectTo: 'membership/portal'
    },

    // Auth routes for guests
    {
        path: '',
        canMatch: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.module').then(m => m.AuthConfirmationRequiredModule)},
            {path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.module').then(m => m.AuthForgotPasswordModule)},
            {path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.module').then(m => m.AuthResetPasswordModule)},
            {path: 'login', loadChildren: () => import('app/modules/auth/sign-in/sign-in.module').then(m => m.AuthSignInModule)},
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canMatch: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.module').then(m => m.AuthSignOutModule)},
        ]
    },


    //######################################################################################################################################
    //  DASHBOARDS ROUTES ##################################################################################################################
    //######################################################################################################################################

    //  HOME DASHBOARD ########
    {
        path: '',
        canMatch: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {path: 'home', loadChildren: () => import('app/modules/admin/dashboard/dashboard.module').then(m => m.DashboardModule)},
        ]
    },

    //######################################################################################################################################
    //  ACCOUNTS AND ACCESS #################################################################################################################
    //######################################################################################################################################

    //  USERS ########

    {
        path: '',
        canMatch: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'users',
                canMatch: [RolesAccessGuard],
                children: [
                    {path: '', loadChildren: () => import('app/modules/admin/general/users/users.module').then(m => m.UsersModule)},
                    {path: 'settings', loadChildren: () => import('app/modules/admin/general/access-control/access-control.module').then(m => m.AccessControlModule)},
                ]
            },
        ]
    },

    //######################################################################################################################################
    //  AUDIT TRAIL + HEALTH (CONFIG) #####################################################################################################
    //######################################################################################################################################

    //  TRILHA DE AUDITORIA + SAÚDE DO SISTEMA ########
    //  Trilha de Auditoria: acessada pelo modal "Configurações" do menu de
    //  usuário (decisão do usuário — NÃO há item de sidebar). "Saúde do
    //  Sistema" (/health): item de menu data-driven do backend (link /health,
    //  screen_key `aud-access`) — a antiga rota `/config/audit-errors`
    //  (mesma tela) virou redirect permanente para `/health`.
    //  Guard: apenas AuthGuard. O gate real é SERVER-SIDE via screen_key
    //  `aud-access` (403 ACCESS_DENIED → interceptor → /home). O
    //  RolesAccessGuard (data-driven pelo menu) NÃO é aplicado: ele bloqueia
    //  o deep-link quando o item de menu não existe (caso dos deep-links a
    //  partir do modal de Configurações) e o gate autêntico é o do backend.
    {
        path: '',
        canMatch: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'config',
                children: [
                    {path: 'audit', loadChildren: () => import('app/modules/admin/config/audit/audit-trail.module').then(m => m.AuditTrailModule)},
                    {path: 'audit-errors', redirectTo: '/health'},
                ]
            },
            {
                path: 'health',
                loadChildren: () => import('app/modules/admin/config/health/health.module').then(m => m.HealthModule),
            },
        ]
    },

    //######################################################################################################################################
    //  MEMBER PORTAL (ADMIN) ##############################################################################################################
    //######################################################################################################################################

    //  PORTAL DO MEMBRO ########
    //  Tela admin de configuração (QR do link público, ativar/desativar,
    //  tipos de relatório). Item de menu backend: link /member-portal,
    //  screen_key member_portal (módulo Configurações).
    {
        path: '',
        canMatch: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'member-portal',
                canMatch: [RolesAccessGuard],
                children: [
                    {path: '', loadChildren: () => import('app/modules/admin/member-portal/member-portal.module').then(m => m.MemberPortalModule)},
                ]
            },
        ]
    },


    //######################################################################################################################################
    //  ECCLESIASTICAL DIVISIONS ROUTES ####################################################################################################
    //######################################################################################################################################

    //  DIVISIONS ########

    {
        path: '',
        canMatch: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'ecclesiastical',
                canMatch: [RolesAccessGuard],
                children: [
                    {path: 'divisions', loadChildren: () => import('app/modules/admin/ecclesiastical/divisions/view/view.module').then(m => m.ViewModule)},
                ]
            },
        ]
    },


    //######################################################################################################################################
    //  ECCLESIASTICAL DIVISIONS GROUPS ROUTES ##############################################################################################
    //######################################################################################################################################

    //  DIVISIONS ########

    {
        path: '',
        canMatch: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'ecclesiastical',
                canMatch: [RolesAccessGuard],
                children: [
                    {path: 'groups', loadChildren: () => import('app/modules/admin/ecclesiastical/groups/view/view.module').then(m => m.ViewModule)},
                ]
            },
        ]
    },


    //######################################################################################################################################
    //  FINANCIAL ROUTES ###################################################################################################################
    //######################################################################################################################################

    //  ENTRIES ########

    {
        path: '',
        canMatch: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'financial',
                canMatch: [RolesAccessGuard],
                children: [
                    {path: 'entries', loadChildren: () => import('app/modules/admin/financial/entries/view/view.module').then(m => m.ViewModule)},
                    {path: 'exits', loadChildren: () => import('app/modules/admin/financial/exits/view/view.module').then(m => m.ViewModule)},
                    {path: 'accounts-cards', loadChildren: () => import('app/modules/admin/financial/account-cards/view/view.module').then(m => m.ViewModule)},
                    {path: 'reports', loadChildren: () => import('app/modules/admin/financial/reports/view/view.module').then(m => m.ViewModule)},
                    {path: 'accounting', loadChildren: () => import('app/modules/admin/financial/accounting/view/view.module').then(m => m.ViewModule)},
                    // 7º item de menu do grupo Financeiro (screen_key `ctp-access`).
                    // Módulo novo NASCE dentro do bloco protegido com RolesAccessGuard
                    // (padrão [2026-09-19]) — rota dedicada /financial/accounts-payable/:id
                    // definida no view.routing do próprio módulo.
                    {path: 'accounts-payable', loadChildren: () => import('app/modules/admin/financial/accounts-payable/view/view.module').then(m => m.AccountsPayableViewModule)},
                ]
            },
        ]
    },


    //  RECEIVERS ########

    {
        path: '',
        canMatch: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'financial',
                canMatch: [RolesAccessGuard],
                children: [
                    {path: 'balances', loadChildren: () => import('app/modules/admin/financial/balances/view/view.module').then(m => m.ViewModule)},
                ]
            },
        ]
    },

    //  RECEIPTS (módulo unificado — Opção B T4) ########
    // Rota exposta SEM RolesAccessGuard temporariamente. T5 (devops) vai
    // adicionar `/financial/receipts` ao menu (menu_items) e a screen_key
    // correspondente — quando isso acontecer, mover esta rota para dentro
    // do bloco financeiro acima (com RolesAccessGuard) para herdar permissões.
    {
        path: '',
        canMatch: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'financial',
                children: [
                    {
                        path: 'receipts',
                        loadChildren: () => import('app/modules/admin/financial/receipts/view/view.module').then(m => m.ViewModule),
                    },
                ]
            },
        ]
    },




    //######################################################################################################################################
    //  SECRETARY ROUTES ###################################################################################################################
    //######################################################################################################################################

    //  MEMBERS ########

    {
        path: '',
        canMatch: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'secretary',
                canMatch: [RolesAccessGuard],
                children: [
                    {path: 'membership', loadChildren: () => import('app/modules/admin/secretary/membership/list/list.module').then(m => m.ListModule)},
                    {path: 'documents', loadChildren: () => import('app/modules/admin/secretary/documents/list/list.module').then(m => m.ListModule)},
                ]
            },
        ]
    },



    //######################################################################################################################################
    //  TEO AI ASSISTANT ###################################################################################################################
    //######################################################################################################################################

    //  TEO ########

    {
        path: 'teo',
        canMatch: [AuthGuard, RolesAccessGuard],
        loadChildren: () => import('app/layout/common/teo/teo.routing').then(m => m.teoRoutes)
    },


    //######################################################################################################################################
    //  NOTIFICATIONS (IN-APP) #############################################################################################################
    //######################################################################################################################################

    //  NOTIFICAÇÕES ########
    //  Tela dedicada de notificações (acesso SÓ pelo botão "Ver todas" do sino —
    //  decisão de produto: sem item de menu, sem screen_key; a API /notifications
    //  é liberada a qualquer usuário autenticado, então NÃO usa RolesAccessGuard).
    {
        path: '',
        canMatch: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'notifications',
                loadChildren: () => import('app/modules/admin/notifications/notifications.module').then(m => m.NotificationsModule),
            },
        ]
    },


    //######################################################################################################################################
    //  TEST / DEV ROUTES ##################################################################################################################
    //######################################################################################################################################

    //  SIMULADOR DE PERMISSÕES ########
    {
        path: '',
        canMatch: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'test',
                children: [
                    {
                        path: 'permissions',
                        loadChildren: () => import('app/modules/test/permissions/permissions.module').then(m => m.PermissionsModule),
                    },
                ]
            },
        ]
    },

    //######################################################################################################################################
    //  GENERAL ROUTES #####################################################################################################################
    //######################################################################################################################################

    //Redirect to / path when the route typed don't exist
    {path: '**', pathMatch : 'full', redirectTo: 'home'},
];
